/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: REMOVE this eslint-disable once we complete development. Some variables are just not used yet.
import './CalculatedKPIBuilder.scss';

import { useDimensionSearch } from '@amzn/gft-dimension-picker';
import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import {
  CalculatedKPIBuilderHandle,
  CalculatedKPIBuilderProps,
  CaretPositionChangeType,
  KpiFormulaNode,
  KpiFormulaNodeType,
} from '@deckManagement/types/CalculatedKPIBuilder.types';
import { Box, Divider, FormHelperText, InputLabel, ListItemText, Paper, Popper, Stack } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { cloneDeep, findLast, forEach, isNil, isNull } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import {
  KeyboardEvent,
  MutableRefObject,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import KpiSegmentInput from './KpiSegmentInput';
import { formulaToKpiSegments, quoteIfNeeded, validateKpiFormula } from './utils/formulaHelpers';

// Tracked Math Operators
const MATH_OPERATORS = ['+', '-', '*', '/', '(', ')'];

// Default Debug Mode to use when prop is not provided.
// TODO: Can remove this in the future.
const DEBUG_MODE_DEFAULT = false;

/**
 * Finds the next input (in a chosen direction) from list of input references and starting point.
 * When a KpiSegmentInput has an EnhancedDimSumNode attached to it, the input field no longer exists, so this function
 * allows us to skip over the `null` references in the list.
 * @param inputRefs The list of input references
 * @param startIndex The starting point
 * @param direction The direction to scan.
 */
const findNextInput = (
  inputRefs: MutableRefObject<(HTMLInputElement | null)[]>,
  startIndex: number,
  direction: -1 | 1,
) => {
  let nextIndex = startIndex + direction;
  while (nextIndex >= 0 && nextIndex < inputRefs.current.length) {
    const nextInput = inputRefs.current[nextIndex];
    if (!isNull(nextInput)) {
      return nextIndex;
    }
    nextIndex += direction;
  }
  // When nothing found, return -1
  return -1;
};

/**
 * Builds the KPI Segments state from the provided segments.
 * @param segments
 */
const buildKpiSegmentsState = (segments: KpiFormulaNode[]) => {
  const blankSegment = { value: '', type: KpiFormulaNodeType.Unknown };
  const result = [];

  // Add prefix if first is Node
  if (segments[0]?.type === KpiFormulaNodeType.Node) {
    result.push({ ...blankSegment });
  }

  segments.forEach((segment, index) => {
    result.push(segment);

    // Add blank between consecutive Nodes
    const nextSegment = segments[index + 1];
    if (segment.type === KpiFormulaNodeType.Node && nextSegment?.type === KpiFormulaNodeType.Node) {
      result.push({ ...blankSegment });
    }
  });

  // Add suffix if last is Node
  if (segments[segments.length - 1]?.type === KpiFormulaNodeType.Node) {
    result.push({ ...blankSegment });
  }

  return result;
};

/**
 * Calculated KPI Builder Component
 * @param props
 * @constructor
 */
const CalculatedKPIBuilder = ({
  ref,
  ...props
}: CalculatedKPIBuilderProps & { ref?: Ref<CalculatedKPIBuilderHandle> }) => {
  const {
    dimensionName = 'revgp',
    label = 'KPI Formula',
    initialFormula,
    onFormulaChange,
    debug = DEBUG_MODE_DEFAULT,
  } = props;
  const [activeInputIndex, setActiveInputIndex] = useState(-1);
  const [kpiSegments, setKpiSegments] = useState<KpiFormulaNode[]>([{ value: '', type: KpiFormulaNodeType.Unknown }]);
  const [searchResults, setSearchResults] = useState<DimSumEnhancedNode[]>([]);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isFormulaValid] = useState(true);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const groupRef = useRef<HTMLElement | null>(null);
  const [focusShiftFromBlur, setFocusShiftFromBlur] = useState(0);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState<number | undefined>(undefined);
  const searchResultRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const isProgrammaticFocus = useRef(false);
  const pendingRemovalIndex = useRef<number | null>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { performSearch } = useDimensionSearch(dimensionName, undefined, true);

  /**
   * Splits the initialFormula into segments when initialFormula changes.
   */
  useEffect(() => {
    if (initialFormula) {
      const segments = formulaToKpiSegments(initialFormula);
      setKpiSegments(buildKpiSegmentsState(segments));
      setIsInitializing(true);
    }
  }, [initialFormula]);

  /**
   * When isInitializing is `true`, performs a DimSum search to find matching values to the formula segments.
   */
  useEffect(() => {
    if (isInitializing && !isEmpty(kpiSegments)) {
      const initializeDimSumNodes = async () => {
        const promises = kpiSegments.map(async (segment) => {
          const isOperator = MATH_OPERATORS.includes(segment.value);
          if (!isOperator && isNil(segment.dimSumNode)) {
            const localSearchResult = await performSearch(segment.value);
            if (localSearchResult && localSearchResult.length > 0) {
              const [dimSumNode] = localSearchResult;
              return {
                ...segment,
                dimSumNode,
              } as KpiFormulaNode;
            }
          }
          return { ...segment };
        });

        Promise.all(promises).then((results) => {
          setIsInitializing(false);
          setKpiSegments(results);
        });
      };

      initializeDimSumNodes();
    }
  }, [isInitializing, kpiSegments, performSearch]);

  /**
   * When isInitializing is `true`, performs a DimSum search to find matching values to the formula segments.
   */
  useEffect(() => {
    if (isInitializing && !isEmpty(kpiSegments)) {
      const initializeDimSumNodes = async () => {
        const promises = kpiSegments.map(async (segment) => {
          const isOperator = MATH_OPERATORS.includes(segment.value);
          if (!isOperator && isNil(segment.dimSumNode)) {
            const localSearchResult = await performSearch(segment.value);
            if (localSearchResult && localSearchResult.length > 0) {
              // gets the first search result with a name which matches the segment value.
              const dimSumNode = localSearchResult.find((node) => node.attributes.name === segment.value);
              return {
                ...segment,
                dimSumNode,
              } as KpiFormulaNode;
            }
          }
          return { ...segment };
        });

        Promise.all(promises).then((results) => {
          setIsInitializing(false);
          setKpiSegments(results);
        });
      };

      initializeDimSumNodes();
    }
  }, [isInitializing, kpiSegments, performSearch]);

  /**
   * Handle delayed removal of empty segments after focus has been established
   */
  useEffect(() => {
    if (pendingRemovalIndex.current !== null && activeInputIndex !== -1) {
      const removalIndex = pendingRemovalIndex.current;
      pendingRemovalIndex.current = null;

      setKpiSegments((prevSegments) => {
        if (
          prevSegments[removalIndex] &&
          prevSegments[removalIndex].value.trim() === '' &&
          removalIndex !== prevSegments.length - 1 &&
          removalIndex !== 0 &&
          // Don't remove blank segments between consecutive NODE fields
          !(
            prevSegments[removalIndex - 1]?.type === KpiFormulaNodeType.Node &&
            prevSegments[removalIndex + 1]?.type === KpiFormulaNodeType.Node
          )
        ) {
          const newSegments: typeof prevSegments = [];
          forEach(prevSegments, (segment, i) => {
            if (i !== removalIndex) {
              newSegments.push(cloneDeep(segment));
            }
          });

          // Adjust activeInputIndex if the removal affects it
          if (removalIndex < activeInputIndex) {
            setActiveInputIndex(activeInputIndex - 1);
            // Refocus on the correct element after removal with proper caret positioning
            focusTimeoutRef.current = setTimeout(() => {
              isProgrammaticFocus.current = true;
              const newIndex = activeInputIndex - 1;
              inputRefs.current[newIndex]?.focus();
              // Place caret at beginning since we're moving to the next field (right arrow behavior)
              inputRefs.current[newIndex]?.setSelectionRange(0, 0);
            }, 10);
          }

          return newSegments;
        }
        return prevSegments;
      });
    }
  }, [activeInputIndex]);

  // Cleanup focusTimeout on unmount only
  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
    };
  }, []);

  /**
   * Generates a formula string from the `kpiSegments` array
   */
  const formulaString = useMemo(() => {
    return kpiSegments
      .map((segment) => {
        if (segment.type === KpiFormulaNodeType.Operator) {
          return segment.value;
        }
        // For node segments, use the DimSum node name if attached, otherwise the raw value.
        // Quote values that contain spaces or operator characters to ensure round-trip safety.
        const nodeValue = segment.dimSumNode?.attributes.name || segment.value;
        return quoteIfNeeded(nodeValue);
      })
      .join(' ')
      .trim();
  }, [kpiSegments]);

  /**
   * Handles auto-scrolling to search result during keyboard navigation of search results.
   */
  useEffect(() => {
    if (selectedAutocompleteIndex && searchResults[selectedAutocompleteIndex]) {
      const selectedNode = searchResults[selectedAutocompleteIndex];
      searchResultRefs.current[selectedNode.nodeID]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [searchResults, selectedAutocompleteIndex]);

  /**
   * Populates search options when the active input segment has a value.
   */
  useEffect(() => {
    let timeoutId: string | number | NodeJS.Timeout | undefined;
    if (activeInputIndex !== -1) {
      const searchForOptions = async () => {
        const currentInput = kpiSegments[activeInputIndex]?.value.trim() || '';
        if (currentInput && currentInput.length > 1) {
          setIsSearchLoading(true);
          setSearchResults([]);
          try {
            const results = await performSearch(currentInput);
            setSearchResults(results || []);
          } catch (error) {
            setSearchResults([]);
          } finally {
            setIsSearchLoading(false);
          }
        } else {
          setSearchResults([]);
          setIsSearchLoading(false);
        }
        setSelectedAutocompleteIndex(undefined);
      };

      timeoutId = setTimeout(searchForOptions, 300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [kpiSegments, activeInputIndex]);

  /**
   * Cleanup inputRef values when `kpiSegments` updates. This keeps the `inputRefs` indexes in-sync with the actual
   * kpiSegments list.
   */
  useEffect(() => {
    inputRefs.current = kpiSegments.map((segment, index) =>
      segment.dimSumNode ? null : inputRefs.current[index] || null,
    );
  }, [kpiSegments]);

  /**
   * Validates the formula and notifies the parent component of changes.
   */
  useEffect(() => {
    if (kpiSegments.length > 0) {
      const { isValid, error } = validateKpiFormula(kpiSegments);
      onFormulaChange?.(formulaString, isValid, isValid ? null : error);
    } else {
      onFormulaChange?.('', false, null);
    }
  }, [formulaString, kpiSegments, onFormulaChange]);

  /**
   * Handles when a new input segment needs to be added after a given `segmentIndex`
   * @param segmentIndex
   */
  const handleAddSegmentRequest = useCallback((segmentIndex: number) => {
    let adjustFocusIndex = 1;
    setKpiSegments((prevSegments) => {
      const newSegments: typeof kpiSegments = [];
      forEach(prevSegments, (segment, i) => {
        const clonedSegment = cloneDeep(segment);
        newSegments.push(clonedSegment);
        const nextSegment = prevSegments[i + 1];
        if (i === segmentIndex) {
          if (clonedSegment.value === '') {
            // do nothing and maintain current input focus. Field is already blank.
            adjustFocusIndex = 0;
          } else if (nextSegment && nextSegment.value === '') {
            // next field is blank, so only change input focus
            adjustFocusIndex = 1;
          } else {
            // add new blank entry and focus on the new input that is created.
            newSegments.push({ value: '', type: KpiFormulaNodeType.Unknown });
            adjustFocusIndex = 1;
          }
        }
      });
      return newSegments;
    });

    // If the input focus adjustment is non-zero, apply it to our current segmentIndex
    if (adjustFocusIndex !== 0) {
      window.setTimeout(() => {
        inputRefs.current[segmentIndex + adjustFocusIndex]?.focus();
      }, 50);
    }
  }, []);

  /**
   * Handles attaching a DimSumNode to a given KPI Input Segment
   * @param segmentIndex The index in the kpiSegments list to use for inserting the new node segment.
   * @param node The node to attach to the segment.
   * @param retainSegmentValue When true, will retain the current value found at `segmentIndex`. Otherwise, the value will be removed.
   */
  const handleAttachNode = useCallback((segmentIndex: number, node: DimSumEnhancedNode, retainSegmentValue = false) => {
    let inputRefIndexOffset = 0;
    setKpiSegments((prevSegments) => {
      const newSegments: typeof kpiSegments = [];
      const newInputRefs: (HTMLInputElement | null)[] = [];
      forEach(prevSegments, (segment, i) => {
        const clonedSegment = cloneDeep(segment);
        if (i === segmentIndex) {
          let retainedSegment = false;

          // Check for retaining the existing segment value
          if (retainSegmentValue && (segment.value !== '' || !isNil(segment.dimSumNode))) {
            retainedSegment = true;
            // Push the existing segment to the list.
            newSegments.push(segment);
            // Keep the existing inputRefs in sync with the shift
            newInputRefs.push(inputRefs.current[i - inputRefIndexOffset]);
            inputRefIndexOffset += 1;
          }
          // Prevents auto-focus adjustment issues
          isProgrammaticFocus.current = true;

          // Assign the required data to our new segment
          clonedSegment.dimSumNode = node;
          clonedSegment.value =
            retainedSegment || clonedSegment.value === '' ? node.attributes.name : clonedSegment.value;
          clonedSegment.type = KpiFormulaNodeType.Node;

          // Code below handles ensuring there are editable input fields around the new segment.
          // This allows the customer to add operators or inject other nodes where necessary.
          const prevSegment = newSegments[newSegments.length - 1];
          const nextSegment = prevSegments[i + 1];

          // Checking for a valid input field BEFORE the new segment entry.
          if (isNil(prevSegment) || prevSegment?.type === KpiFormulaNodeType.Node) {
            // Add a blank segment
            newSegments.push({ value: '', type: KpiFormulaNodeType.Unknown });
            newInputRefs.push(null);
            inputRefIndexOffset += 1;
          }

          // Push our new segment
          newSegments.push(clonedSegment);

          // NOTE: do not use inputRefIndexOffset here
          newInputRefs.push(inputRefs.current[i]);

          // Checking for a valid input field AFTER the new segment entry.
          if (
            isNil(nextSegment) ||
            nextSegment?.type === KpiFormulaNodeType.Node ||
            (nextSegment && nextSegment.value !== '')
          ) {
            // Add a blank segment
            newSegments.push({ value: '', type: KpiFormulaNodeType.Unknown });
            newInputRefs.push(null);
          }
        } else {
          // This segment should remain unchanged
          newSegments.push(clonedSegment);
          newInputRefs.push(inputRefs.current[i - inputRefIndexOffset]);
        }
      });

      inputRefs.current = newInputRefs;
      return newSegments;
    });

    window.setTimeout(() => {
      // Focus on the input segment directly after our newly attached Node segment.
      inputRefs.current[segmentIndex + inputRefIndexOffset + 1]?.focus();
    }, 50);
  }, []);

  /**
   * Handles detaching a DimSumNode from a given KPI Input Segment
   * @param segmentIndex
   */
  const handleDetachNode = useCallback((segmentIndex: number) => {
    setKpiSegments((prevSegments) => {
      const newSegments: typeof kpiSegments = [];
      forEach(prevSegments, (segment, i) => {
        const clonedSegment = cloneDeep(segment);
        newSegments.push(clonedSegment);
        if (i === segmentIndex) {
          clonedSegment.dimSumNode = undefined;
          clonedSegment.type = KpiFormulaNodeType.Unknown;
        }
      });
      return newSegments;
    });

    window.setTimeout(() => {
      const targetInput = inputRefs.current[segmentIndex];
      if (targetInput) {
        targetInput.focus();
        targetInput.setSelectionRange(0, targetInput.value.length);
      }
    }, 50);
  }, []);

  /**
   * Handles assigning focus to a different input segment (`targetIndex`), and determines where the input caret should
   * reside within the input field based on the `direction`
   * @param targetIndex
   * @param direction
   */
  const assignFocus = useCallback(
    (targetIndex: number, direction: CaretPositionChangeType) => {
      console.debug(
        `Moving to index ${targetIndex}`,
        inputRefs.current[targetIndex],
        'focusShift:',
        focusShiftFromBlur,
      );
      // ensure the input caret is at correct position on the focused input
      const moveCaretToStart = [CaretPositionChangeType.Beginning, CaretPositionChangeType.Next].includes(direction);
      const realTargetIndex = targetIndex + focusShiftFromBlur;
      inputRefs.current[realTargetIndex]?.focus();
      setFocusShiftFromBlur(0);
      inputRefs.current[realTargetIndex]?.setSelectionRange(
        moveCaretToStart ? 0 : kpiSegments[targetIndex]?.value.length || 0,
        moveCaretToStart ? 0 : kpiSegments[targetIndex]?.value.length || 0,
      );
    },
    [focusShiftFromBlur, kpiSegments],
  );

  /**
   * Handles when the input caret needs to move to another input field in the kpiSegment list. This is designed to give
   * the user the feeling of a single input field (despite internally being a series of input segments)
   * @param segmentIndex
   * @param direction
   * @param deleteKeyPress (optional) Indicates if the keypress was a delete key. This is used to check for
   *  "backspacing" into a segment which is not immediately editable (such as one that is a Chip)
   */
  const handleCaretPositionChangeRequest = useCallback(
    (segmentIndex: number, direction: CaretPositionChangeType, deleteKeyPress?: boolean) => {
      let targetIndex = segmentIndex + direction;
      console.debug(`Position Change (index: ${segmentIndex}):`, direction, deleteKeyPress);
      if (direction === CaretPositionChangeType.Beginning) {
        // Gets the first editable input segment
        targetIndex = findNextInput(inputRefs, 1, -1);
      } else if (direction === CaretPositionChangeType.End) {
        // Gets the last editable input segment
        targetIndex = findNextInput(inputRefs, kpiSegments.length, -1);
      } else if (
        direction === CaretPositionChangeType.Previous &&
        deleteKeyPress &&
        kpiSegments[segmentIndex + direction]?.dimSumNode
      ) {
        // User pressed "Backspace" into a Chip segment, so we detach the dimSumNode to make it editable again.
        handleDetachNode(segmentIndex + direction);
        targetIndex = -1; // Making this -1 because `handleDetachNode()` handles input focus on its own.
      } else {
        targetIndex = findNextInput(inputRefs, segmentIndex, direction);
      }

      if (targetIndex !== -1 && targetIndex !== segmentIndex) {
        window.setTimeout(() => assignFocus(targetIndex, direction), 10);
      }
    },
    [assignFocus, handleDetachNode, kpiSegments],
  );

  /**
   * Handles keyboard navigation of populated autocomplete list items.
   * @param segmentIndex
   * @param event
   */
  const handleInputKeyDown = useCallback(
    (segmentIndex: number, event: KeyboardEvent<HTMLInputElement>) => {
      // Sanity check to make sure the segment triggering this event is our tracked active input.
      if (segmentIndex === activeInputIndex) {
        if (event.key === 'ArrowDown') {
          // Select the next available autocomplete option
          event.preventDefault();
          setSelectedAutocompleteIndex((prevIndex) =>
            Math.min(prevIndex === undefined ? 0 : prevIndex + 1, searchResults.length - 1),
          );
        } else if (event.key === 'ArrowUp') {
          // Select the previous available autocomplete option
          event.preventDefault();
          setSelectedAutocompleteIndex((prevIndex) =>
            prevIndex === undefined || prevIndex === 0 ? undefined : prevIndex - 1,
          );
        } else if ((event.key === 'Enter' || event.key === 'Tab') && selectedAutocompleteIndex !== undefined) {
          // Confirms the selected autocomplete option by attaching the DimSumNode to the input segment.
          event.preventDefault();
          const selectedNode = searchResults[selectedAutocompleteIndex];
          handleAttachNode(segmentIndex, selectedNode);
          handleCaretPositionChangeRequest(activeInputIndex, CaretPositionChangeType.Next, false);
          setSelectedAutocompleteIndex(undefined);
        }
      }
    },
    [activeInputIndex, handleAttachNode, handleCaretPositionChangeRequest, searchResults, selectedAutocompleteIndex],
  );

  /**
   * Callback that is triggered when a `KpiSegmentInput` receives an Input Focus event.
   * The main purpose of this callback is for this component to track which of the segmented inputs currently has focus
   * by the user.
   * Additionally, when the previously-focused input's value is empty, we should remove it from `kpiSegments` to prevent
   * having excessive empty input fields in the middle of our kpiSegment list.
   * @param segmentIndex
   */
  const handleFocusChange = useCallback(
    (segmentIndex: number) => {
      console.debug('[INIT] handleFocusChange', segmentIndex, 'current activeInputIndex:', activeInputIndex);

      // Skip removal logic for programmatic focus changes, but still update activeInputIndex
      if (isProgrammaticFocus.current) {
        isProgrammaticFocus.current = false;
        setActiveInputIndex(segmentIndex);
        return;
      }

      setActiveInputIndex((prevIndex) => {
        console.debug('FOCUS CHANGING TO INDEX:', segmentIndex, 'from prevIndex:', prevIndex);

        // Check if we should schedule a removal after focus is established
        if (
          prevIndex !== -1 &&
          prevIndex !== segmentIndex &&
          kpiSegments[prevIndex] &&
          kpiSegments[prevIndex].value.trim() === '' &&
          prevIndex !== kpiSegments.length - 1 &&
          prevIndex !== 0 &&
          // Don't remove blank segments between consecutive NODE fields - users need them to add operators
          !(
            kpiSegments[prevIndex - 1]?.type === KpiFormulaNodeType.Node &&
            kpiSegments[prevIndex + 1]?.type === KpiFormulaNodeType.Node
          )
        ) {
          console.debug('Scheduling removal of index:', prevIndex);
          pendingRemovalIndex.current = prevIndex;
        }

        return segmentIndex;
      });
    },
    [activeInputIndex, kpiSegments],
  );

  /**
   * Handles the input changes for each KpiSegments
   * @param segmentIndex
   * @param value
   */
  const handleInputChange = useCallback(
    (segmentIndex: number, value: string) => {
      setKpiSegments((prevSegments) => {
        const newSegments: typeof kpiSegments = [];
        forEach(prevSegments, (segment, i) => {
          const clonedSegment = cloneDeep(segment);
          if (i === segmentIndex) {
            // Updates the value
            clonedSegment.value = value.trimStart();
          }

          // Cleans up multiple blank input fields from being rendered next to each other.
          if (
            clonedSegment.value.trim() === '' &&
            newSegments.length > 0 &&
            newSegments[newSegments.length - 1].value.trim() === ''
          ) {
            // If we're about to remove the current active input, we need to also change the input focus.
            if (activeInputIndex === i) {
              setTimeout(() => {
                console.debug('focusing on:', inputRefs.current[i - 1]);
                inputRefs.current[i - 1]?.focus();
              }, 10);
            }

            return;
          }

          // Checks if the input value begins with a MATH_OPERATOR. If it does, auto-create a new KpiSegment after it.
          if (i === segmentIndex && MATH_OPERATORS.includes(clonedSegment.value.charAt(0))) {
            clonedSegment.value = value.charAt(0);
            clonedSegment.type = KpiFormulaNodeType.Operator;
            newSegments.push(clonedSegment);
            newSegments.push({ value: value.substring(1), type: segment.type || KpiFormulaNodeType.Unknown });
            // Leaving this in for now. When user types a starting parenthesis, we also auto-create the ending parenthesis.
            // Can remove this later on, if desired.
            if (clonedSegment.value === '(') {
              newSegments.push({ value: ')', type: KpiFormulaNodeType.Operator });
            }

            // TODO: Move focus handling to its own function. This pattern exists multiple times throughout.
            setTimeout(() => {
              inputRefs.current[i + 1]?.focus();
            }, 20);
          } else {
            newSegments.push(clonedSegment);
          }
        });

        // Ensures there is always a blank input segment at the end.
        if (newSegments.length > 0 && newSegments.at(-1)!.value.trim() !== '') {
          newSegments.push({ value: '', type: KpiFormulaNodeType.Unknown });
        }

        // Ensures there is always an input segment at the beginning.
        if (newSegments.length > 0 && newSegments.at(0)?.type === KpiFormulaNodeType.Node) {
          newSegments.unshift({ value: '', type: KpiFormulaNodeType.Unknown });
        }

        return newSegments;
      });
    },
    [activeInputIndex],
  );

  /**
   * Handles rendering the individual KpiSegmentInput components
   */
  const renderKpiSegments = useCallback(() => {
    return kpiSegments.map((segment, index) => {
      // Apply insertion class to empty segments for zero-width styling
      const isInsertionPoint = segment.value === '' && segment.type === KpiFormulaNodeType.Unknown;
      const className = isInsertionPoint
        ? 'calculated-kpi-builder__input calculated-kpi-builder__input--insertion'
        : 'calculated-kpi-builder__input';

      return (
        <KpiSegmentInput
          key={`kpi-segment-${index}-${segment.type}-${segment.dimSumNode?.nodeID}`}
          segmentIndex={index}
          value={segment.value}
          dimSumNode={segment.dimSumNode}
          inputRef={(el) => {
            inputRefs.current[index] = el;
          }}
          fullWidth={kpiSegments.length === 1}
          className={`${className} ${index === activeInputIndex ? 'Mui-focused' : ''}`}
          onCaretPositionChangeRequest={handleCaretPositionChangeRequest}
          onKeyDown={handleInputKeyDown}
          onAddSegmentRequest={handleAddSegmentRequest}
          onFocusChange={handleFocusChange}
          onValueChange={handleInputChange}
          handleDetachNode={handleDetachNode}
        />
      );
    });
  }, [
    activeInputIndex,
    handleAddSegmentRequest,
    handleCaretPositionChangeRequest,
    handleDetachNode,
    handleFocusChange,
    handleInputChange,
    handleInputKeyDown,
    kpiSegments,
  ]);

  /**
   * Handles inserting a DimSumNode into the formula at the current active input index.
   *
   * If the active input index is less than zero (i.e.: no focus) then use the last index in kpiSegments.
   * @param node The DimSumEnhancedNode to insert into the formula.
   */
  const insertNodeIntoFormula = useCallback<CalculatedKPIBuilderHandle['insertNodeIntoFormula']>(
    (node) => {
      const targetIndex = activeInputIndex >= 0 ? activeInputIndex : kpiSegments.length - 1;
      handleAttachNode(targetIndex, node, true);
    },
    [activeInputIndex, handleAttachNode, kpiSegments.length],
  );

  /**
   * Exposes internal component functions to parent components via ref
   */
  useImperativeHandle(ref, () => ({
    insertNodeIntoFormula,
  }));

  /**
   * In pixels, the width to use for the Search Results <Popper> (when activated).
   * Derived from the inner content width of `groupRef` via ResizeObserver.
   */
  const [popperWidth, setPopperWidth] = useState(100);

  useEffect(() => {
    const element = groupRef.current;
    if (!element) return;

    const updateWidth = () => {
      const styles = window.getComputedStyle(element);
      const paddingLeft = parseFloat(styles.getPropertyValue('padding-left') || '0');
      const paddingRight = parseFloat(styles.getPropertyValue('padding-right') || '0');
      setPopperWidth((element.clientWidth || 100) - paddingLeft - paddingRight);
    };

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    updateWidth(); // Initial measurement

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Stack spacing={2} divider={<Divider orientation='horizontal' flexItem />}>
        <Box
          className='calculated-kpi-builder__root'
          onClick={() => {
            // Focus the last available input field
            const lastInput = findLast(inputRefs.current, (input) => input !== null);
            lastInput?.focus();
          }}
        >
          <InputLabel className='calculated-kpi-builder__label'>{label}</InputLabel>
          <Box ref={groupRef} className='calculated-kpi-builder__container'>
            {isInitializing ? 'Loading...' : renderKpiSegments()}
          </Box>
          <FormHelperText>
            You are allowed to use the plus (+), minus (-), divide (/), and multiply (*) operations. To force a certain
            order of operations, parenthesis can be used.
          </FormHelperText>
        </Box>
        {!isFormulaValid && <Box className='calculated-kpi-builder__error'>Invalid formula syntax</Box>}

        {/* Debug section */}
        {debug && (
          <Box className='calculated-kpi-builder__debug'>
            <div>Active Input Index: {activeInputIndex}</div>
            <div>Segments: {kpiSegments.filter((segment) => segment.value !== '').length}</div>
            <div>Formula: {formulaString}</div>
          </Box>
        )}
      </Stack>

      {(isSearchLoading || !isEmpty(searchResults)) && (
        <Popper open anchorEl={groupRef.current} placement='bottom' className='calculated-kpi-builder__popper'>
          <Paper elevation={3} className='calculated-kpi-builder__popper-paper'>
            <List style={{ width: popperWidth }} dense>
              {isSearchLoading && (
                <ListItem>
                  <ListItemText
                    primary='Loading Search Results... '
                    slotProps={{ primary: { color: 'text.secondary' } }}
                  />
                </ListItem>
              )}
              {searchResults.map((node, itemIndex) => (
                <ListItemButton
                  key={node.nodeID}
                  ref={(el) => {
                    searchResultRefs.current[node.nodeID] = el;
                  }}
                  selected={selectedAutocompleteIndex === itemIndex}
                  onClick={() => {
                    handleAttachNode(activeInputIndex, node);
                    handleCaretPositionChangeRequest(activeInputIndex, CaretPositionChangeType.Next, false);
                  }}
                >
                  <ListItemText primary={`${node.attributes?.name} - ${node.attributes?.description}`} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      )}
    </>
  );
};

export default CalculatedKPIBuilder;
